import { GroupingOptionsV2 } from '@vae/server/src/utils/arrays/types/GroupingOptions'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../../../analytics/utils/firebaseUtils'
import { ConsumptionDataContext } from '../../../../../contexts/ConsumptionDataContext'
import useUser from '../../../../../contexts/hooks/useUser'
import { getMonthString, parseWeekDay } from '../../../../../modules/consumptionDataParser'

dayjs.extend(isSameOrAfter)

export type ProductionChartTimeHook = ReturnType<typeof useProductionChartTime>

export const useProductionChartTime = (
	defaultSpan: GroupingOptionsV2 = 'day',
	useLastAvailableConsumptionData = false
) => {
	const { lastAvailableConsumptionData } = useContext(ConsumptionDataContext)

	const [activeTimespan, setActiveTimespan] = useState<GroupingOptionsV2>(defaultSpan)
	const [time, setTime] = useState<dayjs.Dayjs>(dayjs().startOf('day'))

	const { user } = useUser()
	const { t } = useTranslation()

	useEffect(() => {
		if (!useLastAvailableConsumptionData || !lastAvailableConsumptionData) return setTime(dayjs())

		setTime(lastAvailableConsumptionData)
	}, [lastAvailableConsumptionData])

	const next = () => {
		logEvent('click-chart-time-next')
		setTime(value => value?.add(1, activeTimespan))
	}

	const back = () => {
		logEvent('click-chart-time-back')
		setTime(value => value?.subtract(1, activeTimespan))
	}

	const startTime = useMemo(() => {
		const processingSpan = activeTimespan === 'week' ? 'isoWeek' : activeTimespan
		return time.startOf(processingSpan)
	}, [activeTimespan, time])

	const endTime = useMemo(() => {
		const processingSpan = activeTimespan === 'week' ? 'isoWeek' : activeTimespan
		let end = time.endOf(processingSpan)

		// Handle edge case for months with fewer than 31 days
		if (activeTimespan === 'month' && end.date() !== time.daysInMonth()) {
			end = time.date(time.daysInMonth()).endOf('day')
		}

		return end
	}, [activeTimespan, time])

	const differenceToPresentDay = useMemo(
		() => time.startOf('day').diff(dayjs().startOf('day'), 'days'),
		[time]
	)

	const timeString = useMemo(() => {
		if (!time) return ''
		const stringFormat = 'DD.MM.YYYY'

		switch (activeTimespan) {
			case 'day':
				switch (differenceToPresentDay) {
					case 1:
						return t('tomorrow')
					case 0:
						return t('today')
					case -1:
						return t('yesterday')
					default:
						return `${parseWeekDay(time.isoWeekday(), user?.language ?? 'fi')} ${time.format(stringFormat)}`
				}
			case 'week':
				return `${time.startOf('isoWeek').format(stringFormat)} - ${time
					.endOf('isoWeek')
					.format(stringFormat)}`

			case 'month':
				return getMonthString(time.month() + 1, user?.language ?? 'fi').concat(' ' + time.year())
			case 'year':
				return time.format('YYYY')

			default:
				return ''
		}
		// eslint-disable-next-line
	}, [time, activeTimespan, user?.language])

	return {
		dayjs: time,
		set: setTime,

		span: activeTimespan,
		setSpan: setActiveTimespan,

		next,
		back,

		differenceToPresentDay,

		startTime,
		endTime,

		timeString,
	}
}
